import React, { useEffect, useCallback } from 'react'
import { Button, Modal } from 'react-bootstrap'
import qs from 'qs'
import { gradebook as endpoints } from '@URLS'
import { client } from '@Utils'

import { LoadingSpinner } from '@CommonComponents'
import { useStateValue } from '@State'

import { ViewOptions } from './ViewOptions'
import { GradebookTable } from './GradebookTable'

export const Gradebook = () => {
  const [currentState, dispatch] = useStateValue()

  const updateDimensions = useCallback(() => {
    const container = $('#gradebook_table_container')
    const gradebookTopLeft = $('div#gradebook_top_left')
    const gradebookTopRight = $('div#gradebook_top_right')
    const gradebookBottomLeft = $('div#gradebook_bottom_left')
    const gradebookBottomRight = $('div#gradebook_bottom_right')

    const containerWidth = container.innerWidth()
    const leftWidth = gradebookTopLeft.width()
    const rightWidth = containerWidth - leftWidth

    try {
      const leftTable = gradebookBottomLeft.find('table')
      const contentHeight = leftTable.height()

      const maxHeight = 600
      const minHeight = 100
      const bottomHeight = Math.min(
        maxHeight,
        Math.max(minHeight, contentHeight + 20)
      )

      gradebookBottomLeft.height(bottomHeight)
      gradebookBottomRight.height(bottomHeight)

      // Get scrollbar width from the bottom right div
      const scrollbarWidth =
        gradebookBottomRight[0].offsetWidth -
        gradebookBottomRight[0].clientWidth

      // Adjust right width to account for scrollbar
      gradebookTopRight.width(rightWidth - scrollbarWidth)
      gradebookBottomRight.width(rightWidth)

      $('.gradebook_table').removeClass('calculating-dimensions').addClass('dimensions-applied')
    } catch (error) {
      // Fallback to a reasonable default
      gradebookBottomLeft.height(400)
      gradebookBottomRight.height(400)
      gradebookTopRight.width(rightWidth)
      gradebookBottomRight.width(rightWidth)
      $('.gradebook_table').removeClass('calculating-dimensions').addClass('dimensions-applied')
    }
  }, [])

  const handleScroll = useCallback(() => {
    const gradebookBottomRight = document.getElementById(
      'gradebook_bottom_right'
    )
    const gradebookTopRight = document.getElementById('gradebook_top_right')
    const gradebookBottomLeft = document.getElementById('gradebook_bottom_left')

    if (!gradebookBottomRight || !gradebookTopRight || !gradebookBottomLeft)
      return

    const scrollHandler = function () {
      gradebookTopRight.scrollLeft = gradebookBottomRight.scrollLeft
      gradebookBottomLeft.scrollTop = gradebookBottomRight.scrollTop
    }

    gradebookBottomRight.addEventListener('scroll', scrollHandler)

    return () => {
      gradebookBottomRight.removeEventListener('scroll', scrollHandler)
    }
  }, [])

  const handleCopy = useCallback((e) => {
    const selection = window.getSelection()
    const selectedText = selection.toString()
    const gradebookContainer = document.getElementById('gradebook_table_container')
    if (!gradebookContainer || !gradebookContainer.contains(selection.anchorNode)) {
      return
    }

    const cleanedText = selectedText
      .replace(/\t+/g, '\t')
      .replace(/\t /g, '\t')
      .replace(/ \t/g, '\t')
      .split('\n')
      .map(line => line.trim())
      .join('\n')

    e.clipboardData.setData('text/plain', cleanedText)
    e.preventDefault()
  }, [])

  useEffect(() => {
    window.gradebookUpdateDimensions = updateDimensions
    window.gradebookHandleScroll = handleScroll

    retrieveData()
    updateDimensions()
    const cleanupScroll = handleScroll()
    window.addEventListener('resize', updateDimensions)

    document.addEventListener('copy', handleCopy)

    return () => {
      window.removeEventListener('resize', updateDimensions)
      document.removeEventListener('copy', handleCopy)
      if (cleanupScroll) cleanupScroll()
      delete window.gradebookUpdateDimensions
      delete window.gradebookHandleScroll
    }
  }, [updateDimensions, handleScroll, handleCopy])

  // Resolves true once the page loads, false after the error message shows.
  const retrieveData = (page = 1) => {
    let data = {
      ...currentState.filters,
      limit: currentState.limit,
      page,
    }
    return fetchGradebookData(data, dispatch, updateDimensions, handleScroll)
  }

  // The pager counts a rejection as a failed page load, and the field goes
  // back to the page that loaded. A failure a later request superseded, such
  // as a filter, settles quietly and leaves that request's page alone.
  const changePage = page => {
    const loadedPage = currentState.page
    const load = retrieveData(page)
    const request = latestRequest
    return load.then(ok => {
      if (ok || request !== latestRequest) return
      dispatch({ type: 'RESTORE_PAGE', payload: loadedPage })
      throw new Error('The gradebook page did not load.')
    })
  }

  let modalBody =
    currentState.modal.modalBody != null ? (
      <div dangerouslySetInnerHTML={{ __html: currentState.modal.modalBody }} />
    ) : (
      <LoadingSpinner style={{ fontSize: '50px' }} />
    )

  return (
    <div>
      <ViewOptions />
      <h1 className="me-page-title">Gradebook</h1>
      <div className="card-myed" style={{ position: 'relative' }}>
        {currentState.gradebook ? (
          <React.Fragment>
            <GradebookTable changePage={changePage} />
            {currentState.loading && (
              <div className="gradebook-loading-overlay">
                <div className="gradebook-loading-content">
                  <LoadingSpinner style={{ fontSize: '50px' }} />
                  <div className="gradebook-loading-text">Loading...</div>
                </div>
              </div>
            )}
          </React.Fragment>
        ) : (
          <LoadingSpinner
            style={{ fontSize: '50px' }}
            outerClass={'spinner-holder'}
          />
        )}
      </div>
      <Modal
        show={currentState.modal.showModal}
        onHide={() =>
          dispatch({
            type: 'UPDATE_MODAL',
            payload: { showModal: false },
          })
        }
      >
        <Modal.Header>
          <Modal.Title>{currentState.modal.selectedActivity.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalBody}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() =>
              dispatch({
                type: 'UPDATE_MODAL',
                payload: { showModal: false },
              })
            }
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export function reportError(message) {
  window.$.meDialog.topMessage(
    `${message} Contact MyEducator support.`,
    6000,
    'danger'
  )
}

// Counts gradebook requests, so a page that fails after a later request
// started, such as a filter, leaves that request's page and overlay alone.
let latestRequest = 0

export const fetchGradebookData = async (
  data,
  dispatch,
  updateDimensions,
  handleScroll
) => {
  const request = ++latestRequest
  try {
    dispatch({
      type: 'CHANGE_PAGE',
      payload: data.page || 1,
    })

    var response = await client.get(
      `${endpoints.index()}?${qs.stringify(data)}`
    )

    if (response.data.error === 'invalid_page') {
      reportError('This is an invalid page.')
      return false
    } else {
      dispatch({
        type: 'UPDATE_GRADEBOOK',
        payload: response.data.gradebook,
      })
      dispatch({
        type: 'UPDATE_ISCANVASCOURSE',
        payload: response.data.isCanvasCourse,
      })
      $('.gradebook_table').addClass('calculating-dimensions').removeClass('dimensions-applied')
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const updateDimFn = updateDimensions || window.gradebookUpdateDimensions
          const handleScrollFn = handleScroll || window.gradebookHandleScroll
          if (updateDimFn) updateDimFn()
          if (handleScrollFn) handleScrollFn()
          dispatch({
            type: 'SET_LOADING',
            payload: false,
          })
        })
      })
      return true
    }
  } catch (error) {
    if (request === latestRequest)
      dispatch({
        type: 'SET_LOADING',
        payload: false,
      })
    reportError('Getting the gradebook data failed.')
    return false
  }
}
